import { OK, SERVICE_UNAVAILABLE } from 'shared/contants';
import { isLinkedDocumentationWithoutCurrentVersionError } from 'shared/utils/errorHandling';

/** Why the server refused a public link, when it says more than the status. */
export enum SharedResourceRefusal {
  /** A link to a documentation (PT-7117) whose documentation has no current version to give. */
  noCurrentVersion = 'noCurrentVersion',
}

export function isNoCurrentVersionRefusal(refusal: SharedResourceRefusal | undefined): boolean {
  return refusal === SharedResourceRefusal.noCurrentVersion;
}

/** Signed URL(s) of a public link, or the status (and reason) the server refused it with. */
export interface SharedResourceSignedRequestResult {
  statusCode: number;
  signedRequest?: string | string[];
  refusal?: SharedResourceRefusal;
}

/** A document shared through a public issue link. */
export interface SharedDocumentSignedUrlRequest {
  issueId: string;
  documentId: string | undefined;
  token: string;
  eventId?: string;
  incomingEmailMessageId?: string;
  linkedDocumentationId?: string;
}

/** A report shared through a public link. */
export interface SharedReportSignedUrlRequest {
  reportId: string;
  token: string;
}

interface SignedRequestResponseBody {
  type?: 'single' | 'multi';
  signedRequest?: string | null;
  signedRequests?: string[] | null;
}

function isSignedRequestResponseBody(body: unknown): body is SignedRequestResponseBody {
  return typeof body === 'object' && body !== null;
}

/**
 * Reads the server's answer to a public link: the signed URL (a list for a
 * multi-file report) on 200, otherwise only the status.
 */
export function readSharedResourceSignedRequest(
  statusCode: number,
  body: unknown
): SharedResourceSignedRequestResult {
  if (statusCode !== OK) {
    return isLinkedDocumentationWithoutCurrentVersionError(body)
      ? { statusCode, refusal: SharedResourceRefusal.noCurrentVersion }
      : { statusCode };
  }
  if (!isSignedRequestResponseBody(body)) {
    return { statusCode: SERVICE_UNAVAILABLE };
  }

  const signedRequest =
    body.type === 'multi'
      ? body.signedRequests ?? undefined
      : body.signedRequest ?? undefined;

  return { statusCode: OK, signedRequest };
}
